'use client';

import { useTranslations } from 'next-intl';
import type { CustomerFindingStatus, InternalFindingStatus } from '../../types';
import { ChangeReasonDialog } from '../change-reason-dialog';
import {
  BadgeStatus,
  FINDING_STATUS_BADGE_STATUS_MAP,
} from '../../components/badge';
import { statusDisplayMap } from './finding-badges-calulcator';

type FindingStatusChangeReasonDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pendingStatus: CustomerFindingStatus | null;
  statusNote: string;
  onStatusNoteChange: (value: string) => void;
  onCancel: () => void;
  onSubmit: () => void;
  isSubmitting?: boolean;
};

export function FindingStatusChangeReasonDialog({
  open,
  onOpenChange,
  pendingStatus,
  statusNote,
  onStatusNoteChange,
  onCancel,
  onSubmit,
  isSubmitting = false,
}: FindingStatusChangeReasonDialogProps) {
  const t = useTranslations('designSystem.findings.findingDrawer');
  const pendingStatusLabel = pendingStatus
    ? statusDisplayMap[pendingStatus as InternalFindingStatus]
    : null;
  const canSubmitStatusUpdate = statusNote.trim().length > 0;

  return (
    <ChangeReasonDialog
      open={open}
      onOpenChange={onOpenChange}
      title={t('statusUpdate.noteDialogTitle')}
      description={
        pendingStatusLabel
          ? t('statusUpdate.noteDialogDescriptionWithStatus', {
              status: pendingStatusLabel,
            })
          : t('statusUpdate.noteDialogDescription')
      }
      selectionLabel={t('statusUpdate.noteDialogSelectionLabel')}
      selection={
        pendingStatusLabel ? (
          <BadgeStatus
            size="sm"
            status={
              FINDING_STATUS_BADGE_STATUS_MAP[
                pendingStatus as InternalFindingStatus
              ]
            }
          >
            {pendingStatusLabel}
          </BadgeStatus>
        ) : undefined
      }
      value={statusNote}
      onValueChange={onStatusNoteChange}
      placeholder={
        pendingStatusLabel
          ? t('statusUpdate.notePlaceholderWithStatus', {
              status: pendingStatusLabel,
            })
          : t('statusUpdate.notePlaceholder')
      }
      cancelLabel={t('statusUpdate.noteCancel')}
      submitLabel={t('statusUpdate.noteSubmit')}
      onCancel={onCancel}
      onSubmit={onSubmit}
      isSubmitting={isSubmitting}
      isSubmitDisabled={!canSubmitStatusUpdate}
      disableCloseWhileSubmitting={true}
    />
  );
}
